import React, { useEffect } from 'react';
import Chart from 'react-apexcharts';
import { numberWithSpaces } from '../../../helpers/index';
import { getYearColors } from '../../../helpers/reportHelpers';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';
import { registerChartInstance, unregisterChartInstance } from '../../../helpers/exportPdf';

const skGraph = require('apexcharts/dist/locales/sk');
const csGraph = require('apexcharts/dist/locales/cs');
const deGraph = require('apexcharts/dist/locales/de');
const enGraph = require('apexcharts/dist/locales/en');

interface IBreakdownRow {
    label: string;
    values: { year: number; value: number }[];
}

interface IProps {
    title: string;
    subtitle?: string;
    columns: { year: number; label: string }[];
    rows: IBreakdownRow[];
    height?: number;
    /** Unique id passed to the ApexCharts `id` option so we can export it later */
    chartId?: string;
}

/**
 * Grouped bar chart - each category (day/week/month) has bars for each year.
 * Used for breakdown views like "daily breakdown within a week".
 */
const ReportGroupedBarChart = ({ title, subtitle, columns, rows, height = 450, chartId }: IProps) => {
    const lingui = useLingui();

    // Cleanup on unmount
    useEffect(() => {
        return () => { if (chartId) unregisterChartInstance(chartId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartId]);

    if (!rows || rows.length === 0 || !columns || columns.length === 0) return null;

    const categories = rows.map(r => r.label);
    const colors = getYearColors(columns.length);

    // Build a series for each year-column
    const series = columns.map((col) => ({
        name: col.label,
        data: rows.map(row => {
            const v = row.values.find(val => val.year === col.year);
            return v ? v.value : 0;
        })
    }));

    return (
        <div className="mgt-30">
            <h3 className="title_h4">{title}</h3>
            {subtitle && <p className="text_s12 color_grey mgt-5">{subtitle}</p>}
            <Chart
                options={{
                    chart: {
                        id: chartId,
                        toolbar: {
                            show: true,
                            tools: {
                                download: '<span><svg class="sico wh16"><use xlink:href="/assets/layout/icons.svg#s_save"></use></svg>'
                            }
                        },
                        locales: [skGraph, csGraph, enGraph, deGraph],
                        defaultLocale: lingui.i18n.locale,
                        events: chartId ? {
                            mounted: (chartInstance: any) => {
                                registerChartInstance(chartId, chartInstance);
                            },
                            updated: (chartInstance: any) => {
                                registerChartInstance(chartId, chartInstance);
                            },
                        } : undefined,
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 3,
                            horizontal: false
                        }
                    },
                    colors,
                    legend: { show: true, position: 'bottom' },
                    dataLabels: { enabled: false },
                    stroke: { show: false },
                    xaxis: {
                        categories,
                        labels: { style: { fontSize: '11px' } }
                    },
                    yaxis: {
                        title: { text: t`Počet návštevníkov` },
                        labels: { formatter: (val) => numberWithSpaces(val) }
                    },
                    grid: { strokeDashArray: 10 },
                    fill: { opacity: 1 },
                    tooltip: {
                        y: { formatter: (val: number) => numberWithSpaces(val) }
                    }
                }}
                series={series}
                type="bar"
                height={height}
            />
        </div>
    );
};

export default ReportGroupedBarChart;